import type {
  ApolloClient,
  ApolloError,
  NormalizedCacheObject,
} from '@apollo/client';
import type { Zone } from '@seek/audience-zones';
import type { Language } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import { useAppConfig } from 'src/modules/appConfig';
import type { Maybe } from 'src/types';
import * as Schema from 'src/types/graphql';

import { useCandidateId } from '../data';

import translations from './.vocab';
import {
  updateSkills,
  updateSkillsFailure,
  updateSkillsSuccess,
} from './actionCreators';
import {
  generateUpdateSkillsOptimisticResponse,
  updateCacheUpdateSkills,
} from './apolloCache';

export const useSkills = () => {
  const { loading, error, data } = useQuery(Schema.GetSkillsDocument);

  const skills = loading || error ? [] : data?.viewer?.skills || [];

  return { loading, error, skills };
};

export const getSkillsFromCache = ({
  apolloClient,
}: {
  apolloClient: ApolloClient<NormalizedCacheObject | Record<string, unknown>>;
}) => {
  const skillsCacheData = getDataFromCache({
    cache: apolloClient.cache,
    query: Schema.GetSkillsDocument,
  });

  return {
    skills: skillsCacheData?.viewer?.skills || [],
  };
};

export const useSuggestedSkills = () => {
  const { zone, language: languageCode } = useAppConfig();

  const { loading, error, data, refetch } = useQuery(
    Schema.GetSuggestedSkillsDocument,
    {
      variables: {
        languageCode,
        zone,
      },
    },
  );

  const suggestedSkills =
    loading || error ? [] : data?.viewer?.suggestedSkills || [];

  return { loading, error, suggestedSkills, refetch };
};

export const getSuggestedSkillsFromCache = ({
  apolloClient,
  languageCode,
  zone,
}: {
  apolloClient: ApolloClient<NormalizedCacheObject | Record<string, unknown>>;
  languageCode: Language;
  zone: Zone;
}) => {
  const suggestedSkillsCacheData = getDataFromCache({
    cache: apolloClient.cache,
    query: Schema.GetSuggestedSkillsDocument,
    variables: {
      languageCode,
      zone,
    },
  });

  return {
    suggestedSkills: suggestedSkillsCacheData?.viewer?.suggestedSkills || [],
  };
};

export const mockGetSkillsRequest = (skills: string[]) => ({
  request: {
    query: Schema.GetSkillsDocument,
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        skills: skills.map((skill) => ({
          keyword: { text: skill, ontologyId: null },
        })),
      },
    },
  },
});

const key = 'skills';

export const useUpdateSkills = () => {
  const { t } = useTranslations(translations);
  const { candidateId } = useCandidateId();
  const dispatch = useDispatch();

  const onMutate = (
    { skillsData: { skills } }: Schema.UpdateSkillsMutationVariables,
    mutationMetadata?: { suggestedSkillsAdded: Maybe<number> },
  ) => {
    dispatch(
      updateSkills(skills, mutationMetadata?.suggestedSkillsAdded || null),
    );
  };

  const onCompleted = () => {
    dispatch(updateSkillsSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(updateSkillsFailure(error));
  };

  return useToastMutation(Schema.UpdateSkillsDocument, {
    key,
    errorActionRouteParameters: { to: 'skills', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Skills updated'),
    update: updateCacheUpdateSkills,
    optimisticResponse: generateUpdateSkillsOptimisticResponse(
      candidateId || '',
    ),
  });
};
